import { useEffect, useRef, useState } from "react";
import { sendRaw } from "../shared/sendRaw.js";
import { cn } from "../ui/index.js";

const HEX = /^#[0-9a-fA-F]{6}$/;

/** The team colour square — click it to pick the jersey colour the score
 *  graphic uses. The square previews while dragging; `team.setColors` goes
 *  out once, on the picker's native `change` (not on every `input`). */
export function TeamColorSwatch({
  slug,
  side,
  color,
  editable,
  className,
}: {
  slug: string;
  side: "home" | "away";
  color: string;
  editable: boolean;
  className?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  // The replicant caught up (or someone else changed it) — drop the preview.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the stored colour changes
  useEffect(() => setDraft(null), [color]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `editable` mounts the input — re-attach to it
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const commit = () => void sendRaw("team.setColors", { slug, side, primary: el.value });
    el.addEventListener("change", commit);
    return () => el.removeEventListener("change", commit);
  }, [slug, side, editable]);

  const shown = draft ?? color;
  const box = "relative inline-block shrink-0 rounded-md border border-border-strong";
  if (!editable) {
    return <span className={cn(box, className)} style={{ background: shown }} aria-hidden="true" />;
  }
  return (
    <label
      title="Rangni o'zgartirish"
      className={cn(box, "cursor-pointer hover:ring-2 hover:ring-accent-strong/50", className)}
      style={{ background: shown }}
    >
      <input
        ref={ref}
        type="color"
        aria-label="Jamoa rangi"
        value={HEX.test(shown) ? shown : "#ffffff"}
        onChange={(e) => setDraft(e.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      />
    </label>
  );
}
